'use client';

import { useTranslations } from 'next-intl';
import { IconButton, Tooltip } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Layers01Icon } from '@terra/ui-icons';
import {
  getAssetMergeBlockReason,
  isAssetMergeReviseSelection,
  type AssetMergeBlockReason,
  type AssetMergeCandidate,
} from '../../utils/asset-merge/asset-merge-selection';
import { ListPageSelectionBar } from '../common/ListPageSelectionBar';

type AssetsBulkActionsBarProps = {
  selectedCount: number;
  mergeCandidates: readonly AssetMergeCandidate[];
  isLoading: boolean;
  onClearSelection: () => void;
  onMerge: () => void;
};
type MergeBlockTooltipKey =
  | 'tooFewTooltip'
  | 'mixedTypeTooltip'
  | 'mixedDomainTooltip'
  | 'mixedSegmentCountTooltip'
  | 'mixedMethodTooltip';
const BLOCK_REASON_KEY: Record<AssetMergeBlockReason, MergeBlockTooltipKey> = {
  tooFew: 'tooFewTooltip',
  mixedType: 'mixedTypeTooltip',
  mixedDomain: 'mixedDomainTooltip',
  mixedSegmentCount: 'mixedSegmentCountTooltip',
  mixedMethod: 'mixedMethodTooltip',
};

export function AssetsBulkActionsBar({
  selectedCount,
  mergeCandidates,
  isLoading,
  onClearSelection,
  onMerge,
}: AssetsBulkActionsBarProps) {
  const t = useTranslations('proxy.assets.merge');
  const blockReason = getAssetMergeBlockReason(mergeCandidates);
  const isMergeDisabled =
    isLoading ||
    blockReason !== undefined ||
    mergeCandidates.length !== selectedCount;
  const actionLabel = isAssetMergeReviseSelection(mergeCandidates)
    ? t('revise')
    : t('merge');
  const mergeTooltip =
    blockReason === undefined ? actionLabel : t(BLOCK_REASON_KEY[blockReason]);

  return (
    <ListPageSelectionBar
      selectedCount={selectedCount}
      clearSelectionLabel={t('clearSelection')}
      onClearSelection={onClearSelection}
    >
      <Tooltip title={mergeTooltip}>
        <Box display="inline-flex" flexShrink={0}>
          <IconButton
            aria-label={actionLabel}
            size="xs"
            variant="ghost"
            disabled={isMergeDisabled}
            onClick={onMerge}
            flexShrink={0}
          >
            <Layers01Icon size={12} />
          </IconButton>
        </Box>
      </Tooltip>
    </ListPageSelectionBar>
  );
}
